import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const POLL_MS = 60000;

// Bell with an unread count and a dropdown of the signed-in user's in-app
// notifications (ticket and knowledge base). Everything is scoped by the
// token on the server, so no ids are sent.
export default function NotificationBell() {
  const [anchor, setAnchor] = useState(null);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    const res = await global.$axios.get("notificaton/web/count");
    if (mounted.current && res?.data?.Status === 200) setCount(res.data.Message || 0);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    const res = await global.$axios.get("notificaton/web/list", { params: { limit: 20 } });
    if (!mounted.current) return;
    setItems(res?.data?.Status === 200 ? res.data.Message : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadCount();
    const timer = setInterval(() => {
      if (!document.hidden) loadCount();
    }, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [loadCount]);

  const open = (event) => {
    setAnchor(event.currentTarget);
    loadList();
    loadCount();
  };

  const openItem = async (item) => {
    // Knowledge base notifications open the article; ticket ones just clear.
    if (item.NotifyType === "kb_article") {
      setAnchor(null);
      navigate(`/app/Knowledge/Article/${item.ForId}`);
    }
    await markRead(item);
  };

  const markRead = async (item) => {
    if (item.Viewed) return;
    setItems((list) =>
      list.map((x) => (x.NotificationId === item.NotificationId ? { ...x, Viewed: true } : x))
    );
    setCount((n) => Math.max(0, n - 1));
    await global.$axios.post("/notificaton/web/read", { NotificationId: item.NotificationId });
  };

  const markAll = async () => {
    setItems((list) => list.map((x) => ({ ...x, Viewed: true })));
    setCount(0);
    await global.$axios.post("/notificaton/web/read-all");
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={open}
          aria-label={`Notifications, ${count} unread`}
          sx={{ border: "1px solid #C5D0DC", borderRadius: "50%", p: 0.75 }}
        >
          <Badge badgeContent={count} color="error" max={99}>
            <NotificationsIcon style={{ color: "#0071BC" }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxWidth: "calc(100vw - 32px)", borderRadius: "12px" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #EEF2F6" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Notifications</Typography>
          <Button size="small" onClick={markAll} disabled={count === 0} sx={{ textTransform: "none", fontWeight: 600 }}>
            Mark all as read
          </Button>
        </Box>
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : items.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center", color: "#5B6B7D", fontSize: 14 }}>
              You're all caught up.
            </Typography>
          ) : (
            items.map((item) => (
              <Box
                key={item.NotificationId}
                component="button"
                type="button"
                onClick={() => openItem(item)}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  width: "100%",
                  textAlign: "left",
                  px: 2,
                  py: 1.5,
                  border: 0,
                  borderBottom: "1px solid #EEF2F6",
                  cursor: "pointer",
                  font: "inherit",
                  background: item.Viewed ? "#FFFFFF" : "#F3F9FE",
                  "&:hover": { background: "#EAF4FC" },
                }}
              >
                <Box sx={{ width: 8, height: 8, mt: "6px", flex: "0 0 auto", borderRadius: "50%", background: item.Viewed ? "transparent" : "#0071BC" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: item.Viewed ? 500 : 600, fontSize: 14, color: "#14202E" }}>
                    {item.NotifyTitle}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#3F4E5F", mt: 0.25, wordBreak: "break-word" }}>
                    {item.Content}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#5B6B7D", mt: 0.5 }}>
                    {moment(item.CreatedAt).fromNow()}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}
